import { useState } from "react";
import { Lightbulb, Eye, EyeOff, ChevronRight } from "@/lib/icons";
import { Button } from "@/components/ui/button";

export interface WorkedStep {
  /** Short label like "Step 1: Identify what's given". */
  label: string;
  /** Plain-English explanation of what happens in this step. */
  explain: string;
  /** Optional formula or equation to render in monospace. */
  formula?: string;
}

interface Props {
  title: string;
  problem: string;
  steps: WorkedStep[];
  answer: string;
}

/**
 * Worked example with progressive step reveal. Students try the problem first,
 * then reveal one step at a time so they don't skip the reasoning.
 */
export default function WorkedExample({ title, problem, steps, answer }: Props) {
  const [open, setOpen] = useState(false);
  const [revealed, setRevealed] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);

  return (
    <div className="rounded-lg border border-border bg-tile-bg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-muted/40 transition-colors"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2">
          <Lightbulb size={16} className="text-primary" aria-hidden="true" />
          <div>
            <div className="text-sm font-semibold text-foreground">
              Worked example: {title}
            </div>
            <div className="text-xs text-muted-foreground">
              Reveal one step at a time - try it yourself first.
            </div>
          </div>
        </div>
        <ChevronRight
          size={14}
          className={`text-muted-foreground transition-transform ${open ? "rotate-90" : ""}`}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div className="px-4 pb-4 pt-2 space-y-3">
          <div className="rounded-md border border-primary/30 bg-primary/5 p-3">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-primary mb-1">
              Problem
            </p>
            <p className="text-sm text-foreground">{problem}</p>
          </div>

          <ol className="space-y-2">
            {steps.slice(0, revealed).map((s, i) => (
              <li
                key={i}
                className="rounded-md border border-border bg-background p-3 space-y-1"
              >
                <p className="text-xs font-semibold text-foreground">{s.label}</p>
                <p className="text-xs text-muted-foreground">{s.explain}</p>
                {s.formula && (
                  <p className="font-mono text-xs text-foreground bg-muted/50 rounded px-2 py-1 mt-1">
                    {s.formula}
                  </p>
                )}
              </li>
            ))}
          </ol>

          <div className="flex flex-wrap items-center gap-2">
            {revealed < steps.length && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                onClick={() => setRevealed((r) => r + 1)}
              >
                <Eye size={12} className="mr-1" aria-hidden="true" />
                Reveal step {revealed + 1} of {steps.length}
              </Button>
            )}
            {revealed === steps.length && !showAnswer && (
              <Button
                size="sm"
                className="h-7 text-xs"
                onClick={() => setShowAnswer(true)}
              >
                <Eye size={12} className="mr-1" aria-hidden="true" />
                Show final answer
              </Button>
            )}
            {(revealed > 0 || showAnswer) && (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-xs"
                onClick={() => {
                  setRevealed(0);
                  setShowAnswer(false);
                }}
              >
                <EyeOff size={12} className="mr-1" aria-hidden="true" />
                Hide all
              </Button>
            )}
          </div>

          {showAnswer && (
            <div className="rounded-md border border-primary/40 bg-primary/10 p-3">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-primary mb-1">
                Answer
              </p>
              <p className="font-mono text-sm text-foreground">{answer}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}